import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Stethoscope, Loader2, Copy, Check, ChevronDown, ChevronUp,
  Clock, ExternalLink, AlertTriangle, Sparkles, ShieldCheck, Mail,
} from "lucide-react";

export default function PreWorkPanel({ job }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!job?.id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/jobs/${job.id}/questionnaire`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [job?.id, job?.questionnaire_submitted_at]);

  if (loading || !data || !data.active) return null;

  const submitted = !!data.submitted_at;
  const answeredRows = (data.rows || []).filter((r) => (r.answer || "").trim());

  const summary = answeredRows
    .map((r) => `• ${r.label}\n  ${r.answer}`)
    .join("\n\n");

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(`Pre-work answers — ${job.customer_name || ""} ${job.vehicle_reg || ""}\n\n${summary}`);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Couldn't copy"); }
  };

  // Quick highlight bullets — first answer is the main issue, then key signal answers
  const headline = (answeredRows.find((r) => r.id === "q1") || {}).answer || "";
  const when = (answeredRows.find((r) => r.id === "q2") || {}).answer || "";
  const freq = (answeredRows.find((r) => r.id === "q3") || {}).answer || "";

  const qPath = `/q/${data.share_token}`;
  const qUrl = typeof window !== "undefined" ? `${window.location.origin}${qPath}` : qPath;

  return (
    <div className={`card-default ${submitted ? "border-warning/50 bg-warning/5" : "border-border"}`} data-testid="prework-panel">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/30 transition-colors text-left"
        data-testid="prework-toggle"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center ${submitted ? "bg-warning/20 text-warning" : "bg-muted/40 text-muted-foreground"}`}>
            <Stethoscope className="w-5 h-5" />
          </div>
          <div>
            <div className="label-overline">{submitted ? "Pre-work answers" : "Awaiting customer"}</div>
            <div className="font-display font-bold text-lg mt-0.5">
              {submitted
                ? <>Customer reports — <span className="text-warning">{answeredRows.length} answers</span></>
                : "Diagnostic questionnaire sent — awaiting reply"}
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">
          {!submitted ? (
            <div className="rounded-lg border border-dashed border-border p-4 flex items-start gap-3 text-sm">
              <Clock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Customer hasn't filled it in yet.</div>
                <p className="text-xs text-muted-foreground mt-1">They received this link with their pre-call reminder. Once they submit, the answers appear here automatically.</p>
                <a href={qUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-primary hover:underline" data-testid="prework-link">
                  <ExternalLink className="w-3 h-3" /> <span className="font-mono-plex truncate">{qPath}</span>
                </a>
              </div>
            </div>
          ) : (
            <>
              {/* Headline strip — instant readable summary */}
              {headline && (
                <div className="rounded-lg bg-warning/10 border border-warning/30 p-4" data-testid="prework-headline">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-warning mt-0.5 shrink-0" />
                    <div className="text-sm leading-relaxed">
                      <span className="font-semibold">"{headline}"</span>
                      {(when || freq) && (
                        <span className="text-muted-foreground"> — {[when, freq && freq.toLowerCase()].filter(Boolean).join(", ")}.</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Q&A grid */}
              <div className="rounded-lg border border-border divide-y divide-border" data-testid="prework-rows">
                {answeredRows.map((r) => (
                  <div key={r.id} className="p-3" data-testid={`prework-row-${r.id}`}>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{r.label}</div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{r.answer}</div>
                  </div>
                ))}
                {answeredRows.length === 0 && (
                  <div className="p-3 text-sm text-muted-foreground italic">Customer submitted with no answers filled in.</div>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px] text-muted-foreground font-mono-plex">
                  Submitted {new Date(data.submitted_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
                <button type="button" onClick={copyAll} className="btn-outline text-xs" data-testid="prework-copy-btn">
                  {copied ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
                  Copy all
                </button>
              </div>

              <div className="rounded-md bg-primary/10 border border-primary/30 p-2.5 text-xs flex items-start gap-2" data-testid="prework-ai-hint">
                <Sparkles className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                <span>The AI customer explanation will open by referring to what they told us here — making them feel heard.</span>
              </div>

              {data.consent && (
                <div className="rounded-lg border border-border bg-muted/20 p-3 text-xs space-y-1.5" data-testid="prework-consent">
                  <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                    <ShieldCheck className="w-3.5 h-3.5" /> GDPR consent
                  </div>
                  <div className="flex items-center gap-2" data-testid="prework-consent-data">
                    {data.consent.data_processing ? (
                      <Check className="w-3.5 h-3.5 text-success shrink-0" />
                    ) : (
                      <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0" />
                    )}
                    <span>{data.consent.data_processing ? "Data processing: agreed" : "Data processing: NOT agreed — do not text/email"}</span>
                  </div>
                  <div className="flex items-center gap-2" data-testid="prework-consent-marketing">
                    {data.consent.marketing ? (
                      <Mail className="w-3.5 h-3.5 text-primary shrink-0" />
                    ) : (
                      <span className="w-3.5 h-3.5 rounded-full border border-muted-foreground/40 shrink-0" />
                    )}
                    <span className="text-muted-foreground">Marketing: {data.consent.marketing ? "opted in — add to MOT/service reminders" : "opted out"}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
